/**
 * Admin Control Panel — /admin
 *
 * Dedicated CMS for the PUBLIC site (landing page text, programmes catalogue,
 * gallery). Uses its own SiteAdminLayout — does NOT share navigation with the
 * LMS or POD admin shells. Edits here publish live to the public site.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import SiteAdminLayout from "../components/SiteAdminLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, PencilSimple, Trash, UploadSimple, ShieldCheck, GraduationCap, Images, Article, House, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_PROG = {
  title: "", description: "", duration: "", fee: 0, min_payment_percent: 60,
  start_date: "", end_date: "", cohort: "", status: "active", thumbnail: "",
};

// Pretty label for the standardized programme statuses (Section 1).
const STATUS_LABEL = { active: "Active", draft: "Draft", inactive: "Inactive" };
const formatStatus = (s) => {
  const k = (s || "active").toLowerCase();
  return STATUS_LABEL[k] || s || "Active";
};

function ProgrammeScheduleForm({ items, onSaved }) {
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState({ start_date: "", end_date: "", price_usd: "", discount_percent: "", enrollment_open: false });
  const [saving, setSaving] = useState(false);

  const sorted = [...(items || [])].sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  const handleSelect = (id) => {
    setSelectedId(id);
    const p = sorted.find(x => x.id === id);
    if (!p) { setForm({ start_date: "", end_date: "", price_usd: "", discount_percent: "", enrollment_open: false }); return; }
    setForm({
      start_date: p.start_date ? String(p.start_date).slice(0, 10) : "",
      end_date:   p.end_date   ? String(p.end_date).slice(0, 10)   : "",
      price_usd:  p.price_usd ?? p.fee ?? "",
      discount_percent: p.discount_percent ?? 0,
      enrollment_open: p.enrollment_open === true,
    });
  };

  const save = async () => {
    if (!selectedId) { toast.error("Pick a programme first"); return; }
    setSaving(true);
    try {
      const payload = {
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        price_usd: form.price_usd === "" ? null : Number(form.price_usd),
        discount_percent: form.discount_percent === "" ? null : Number(form.discount_percent),
        enrollment_open: !!form.enrollment_open,
      };
      // Strip nulls so they're treated as "no change" on the backend.
      Object.keys(payload).forEach(k => payload[k] === null && delete payload[k]);
      await api.patch(`/programmes/${selectedId}/schedule`, payload);
      toast.success("Schedule saved — live on the public site");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const discountedPrice = form.price_usd && form.discount_percent
    ? (Number(form.price_usd) * (1 - Number(form.discount_percent) / 100)).toFixed(2)
    : null;

  return (
    <Card className="border border-amber-200 bg-amber-50/40 rounded-none mb-6" data-testid="admin-programme-schedule-form">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-amber-400 text-slate-950 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">New</div>
          <div>
            <h2 className="text-xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Set Programme Schedule</h2>
            <p className="text-xs text-slate-600 mt-0.5">Pick a programme from the public catalogue, then set its run-dates, price, discount and enrollment toggle. Saves immediately.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Programme</Label>
            <Select value={selectedId} onValueChange={handleSelect}>
              <SelectTrigger className="rounded-none" data-testid="schedule-programme-select">
                <SelectValue placeholder="Choose programme from catalogue…" />
              </SelectTrigger>
              <SelectContent>
                {sorted.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.code ? `${p.code} · ` : ""}{p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div><Label className="text-xs">Start date</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="rounded-none" data-testid="schedule-start-date" /></div>
          <div><Label className="text-xs">End date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="rounded-none" data-testid="schedule-end-date" /></div>
          <div><Label className="text-xs">Price (USD)</Label><Input type="number" min="0" step="1" value={form.price_usd} onChange={e => setForm({ ...form, price_usd: e.target.value })} className="rounded-none" data-testid="schedule-price" /></div>
          <div>
            <Label className="text-xs">Discount %</Label>
            <Input type="number" min="0" max="100" step="1" value={form.discount_percent} onChange={e => setForm({ ...form, discount_percent: e.target.value })} className="rounded-none" data-testid="schedule-discount" />
            {discountedPrice && <p className="text-[11px] text-emerald-700 mt-1">Effective price: <strong>${discountedPrice}</strong></p>}
          </div>

          <div className="md:col-span-2 flex items-center justify-between bg-white border border-slate-200 px-4 py-3 mt-1">
            <div>
              <p className="text-sm font-bold text-slate-950">Enrollment status</p>
              <p className="text-xs text-slate-500 mt-0.5">When OFF, the catalogue shows the card as <strong>Coming Soon</strong> and applies are blocked server-side.</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="schedule-enrollment-toggle">
              <input type="checkbox" checked={form.enrollment_open} onChange={e => setForm({ ...form, enrollment_open: e.target.checked })} className="w-4 h-4" />
              <span className={`text-xs font-bold uppercase tracking-widest ${form.enrollment_open ? "text-emerald-700" : "text-slate-500"}`}>
                {form.enrollment_open ? "Enrolling" : "Not Enrolling"}
              </span>
            </label>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={save} disabled={!selectedId || saving} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2" data-testid="schedule-save-btn">
            {saving ? "Saving…" : "Save schedule"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgrammesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PROG);
  const [saving, setSaving] = useState(false);
  // Catalogue selection drives the pre-fill in the Create dialog. Items already
  // hold the catalogue list so we just track the picked id locally.
  const [catalogueSelection, setCatalogueSelection] = useState("");

  const applyCatalogueSelection = (id) => {
    setCatalogueSelection(id);
    if (!id) return;
    const p = items.find(x => x.id === id);
    if (!p) return;
    setForm(f => ({
      ...f,
      title: p.title || "",
      description: p.description || p.overview || "",
      duration: p.duration || "",
      fee: Number(p.fee ?? p.price_usd ?? 0),
      status: p.status || "active",
    }));
  };

  const load = () => api.get("/programmes").then(r => { setItems(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_PROG); setCatalogueSelection(""); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "", description: p.description || "", duration: p.duration || "",
      fee: Number(p.fee ?? p.price_usd ?? 0), min_payment_percent: Number(p.min_payment_percent ?? 60),
      start_date: p.start_date ? String(p.start_date).slice(0, 10) : "",
      end_date: p.end_date ? String(p.end_date).slice(0, 10) : "",
      cohort: p.cohort || "", status: p.status || "active", thumbnail: p.thumbnail || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    setSaving(true);
    try {
      // Auto-derive enrollment_open from status (active / enrollment_open / running ⇒ true).
      const statusLc = (form.status || "").toLowerCase();
      const enrollment_open = ["active", "enrollment_open", "running"].includes(statusLc);
      const payload = { ...form, fee: Number(form.fee), min_payment_percent: Number(form.min_payment_percent), enrollment_open };
      if (editing) await api.put(`/programmes/${editing.id}`, payload);
      else await api.post("/programmes", payload);
      toast.success(editing ? "Programme updated" : "Programme created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/programmes/${p.id}`);
      toast.success("Programme deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-programmes-panel">
      <ProgrammeScheduleForm items={items} onSaved={load} />

      <div className="flex items-center justify-between mb-4 mt-10">
        <div>
          <h2 className="text-xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Programmes Catalogue</h2>
          <p className="text-xs text-slate-500 mt-0.5">{items.length} programme{items.length === 1 ? "" : "s"} — visible on /programmes-catalogue</p>
        </div>
        <Button onClick={openNew} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2" data-testid="admin-new-programme-btn">
          <Plus size={16} weight="bold" /> New Programme
        </Button>
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2.5 font-bold">Title</th>
                <th className="px-3 py-2.5 font-bold">Duration</th>
                <th className="px-3 py-2.5 font-bold">Price</th>
                <th className="px-3 py-2.5 font-bold">Status</th>
                <th className="px-3 py-2.5 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="border-t border-slate-200 hover:bg-slate-50/60" data-testid={`admin-programme-row-${p.id}`}>
                  <td className="px-3 py-2.5">
                    <p className="font-semibold text-slate-950 truncate max-w-md">{p.title}</p>
                    <p className="text-[11px] text-slate-500 truncate max-w-md">{p.code || "—"}</p>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{p.duration || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">${Number(p.fee ?? p.price_usd ?? 0).toFixed(0)}</td>
                  <td className="px-3 py-2.5"><Badge className="rounded-none text-[10px] capitalize bg-slate-200 text-slate-800">{formatStatus(p.status)}</Badge></td>
                  <td className="px-3 py-2.5 text-right">
                    <a
                      href={`/p/${(p.code || p.id || "").toLowerCase()}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-amber-700 mr-2"
                      data-testid={`admin-view-public-${p.id}`}
                      title="Open public programme page in a new tab"
                    >
                      <ArrowRight size={12} /> View public page
                    </a>
                    <Button onClick={() => openEdit(p)} variant="ghost" size="sm" className="rounded-none gap-1 text-blue-800 hover:bg-blue-50" data-testid={`admin-edit-programme-${p.id}`}><PencilSimple size={14} /> Edit</Button>
                    <Button onClick={() => remove(p)} variant="ghost" size="sm" className="rounded-none gap-1 text-red-700 hover:bg-red-50" data-testid={`admin-delete-programme-${p.id}`}><Trash size={14} /> Delete</Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-sm">No programmes yet. Click <strong>New Programme</strong> to add the first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader><DialogTitle>{editing ? "Edit programme" : "New programme"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editing && (
              <div className="md:col-span-2 bg-amber-50 border border-amber-200 px-3 py-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-amber-700">Select from Catalogue (optional)</Label>
                <Select value={catalogueSelection || "__scratch"} onValueChange={(v) => applyCatalogueSelection(v === "__scratch" ? "" : v)}>
                  <SelectTrigger className="rounded-none bg-white mt-1" data-testid="catalogue-programme-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__scratch">— Create from scratch —</SelectItem>
                    {items.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {`${p.code ? p.code + " · " : ""}${p.title}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-amber-700 mt-1">Pre-fills title, description, duration, fee, status from the picked programme. All fields stay editable.</p>
              </div>
            )}
            <div className="md:col-span-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="programme-form-title" /></div>
            <div className="md:col-span-2"><Label>Description *</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none" data-testid="programme-form-description" /></div>
            <div><Label>Duration</Label><Input placeholder="e.g. 6 Months" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} className="rounded-none" data-testid="programme-form-duration" /></div>
            <div><Label>Cohort label</Label><Input value={form.cohort} onChange={e => setForm({ ...form, cohort: e.target.value })} className="rounded-none" /></div>
            <div><Label>Fee (USD)</Label><Input type="number" min="0" value={form.fee} onChange={e => setForm({ ...form, fee: e.target.value })} className="rounded-none" data-testid="programme-form-fee" /></div>
            <div><Label>Min upfront %</Label><Input type="number" min="0" max="100" value={form.min_payment_percent} onChange={e => setForm({ ...form, min_payment_percent: e.target.value })} className="rounded-none" /></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="rounded-none" /></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="rounded-none" /></div>
            <div className="md:col-span-2"><Label>Thumbnail URL</Label><Input value={form.thumbnail} onChange={e => setForm({ ...form, thumbnail: e.target.value })} className="rounded-none" /></div>
            <div className="md:col-span-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-none" data-testid="programme-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* Section 1 — only three canonical statuses are kept. Legacy values
                      (upcoming/enrollment_open/running/completed/archived) are still
                      accepted by the backend so older rows keep working, but new
                      saves should use one of these three. */}
                  <SelectItem value="active">Active (Now Enrolling)</SelectItem>
                  <SelectItem value="draft">Draft (Hidden)</SelectItem>
                  <SelectItem value="inactive">Inactive (Not In Session)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none" data-testid="programme-form-save">{saving ? "Saving…" : editing ? "Save changes" : "Create programme"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [newItem, setNewItem] = useState({ title: "", description: "", file: null });

  const load = () => api.get("/gallery").then(r => { setItems(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!newItem.file) { toast.error("Pick an image first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", newItem.file);
      fd.append("title", newItem.title || newItem.file.name);
      fd.append("description", newItem.description);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Gallery item uploaded");
      setNewItem({ title: "", description: "", file: null });
      const fileInput = document.getElementById("admin-gallery-file");
      if (fileInput) fileInput.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const openEdit = (it) => { setEditing(it); setEditForm({ title: it.title || "", description: it.description || "" }); setEditOpen(true); };

  const saveEdit = async () => {
    try {
      await api.put(`/gallery/${editing.id}`, editForm);
      toast.success("Gallery item updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title || "this item"}"?`)) return;
    try {
      await api.delete(`/gallery/${it.id}`);
      toast.success("Gallery item deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-gallery-panel">
      <Card className="border border-slate-200 rounded-none mb-6">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-slate-950 mb-3 flex items-center gap-2"><UploadSimple size={16} /> Upload new gallery item</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="Title" value={newItem.title} onChange={e => setNewItem({ ...newItem, title: e.target.value })} className="rounded-none" data-testid="gallery-new-title" />
            <Input placeholder="Description (optional)" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} className="rounded-none" data-testid="gallery-new-description" />
            <Input id="admin-gallery-file" type="file" accept="image/*" onChange={e => setNewItem({ ...newItem, file: e.target.files?.[0] || null })} className="rounded-none" data-testid="gallery-new-file" />
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={upload} disabled={uploading} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2" data-testid="gallery-upload-btn">
              <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Gallery</h2>
        <p className="text-xs text-slate-500">{items.length} item{items.length === 1 ? "" : "s"} — visible on /gallery</p>
      </div>

      {loading ? <p className="text-sm text-slate-400">Loading…</p> : items.length === 0 ? (
        <Card className="border border-slate-200 rounded-none"><CardContent className="p-8 text-center text-sm text-slate-400">No gallery items yet. Upload one above.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(it => (
            <Card key={it.id} className="border border-slate-200 rounded-none overflow-hidden" data-testid={`admin-gallery-card-${it.id}`}>
              <div className="aspect-square bg-slate-100 overflow-hidden">
                {it.image_url ? <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><Images size={32} /></div>}
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-slate-950 truncate" title={it.title}>{it.title || "Untitled"}</p>
                <p className="text-[11px] text-slate-500 truncate" title={it.description}>{it.description || ""}</p>
                <div className="mt-2 flex items-center gap-1">
                  <Button onClick={() => openEdit(it)} variant="ghost" size="sm" className="flex-1 rounded-none text-xs gap-1 text-blue-800 hover:bg-blue-50" data-testid={`admin-gallery-edit-${it.id}`}><PencilSimple size={12} /> Edit</Button>
                  <Button onClick={() => remove(it)} variant="ghost" size="sm" className="flex-1 rounded-none text-xs gap-1 text-red-700 hover:bg-red-50" data-testid={`admin-gallery-delete-${it.id}`}><Trash size={12} /> Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle>Edit gallery item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="rounded-none" data-testid="gallery-edit-title" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="rounded-none" data-testid="gallery-edit-description" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={saveEdit} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none" data-testid="gallery-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SiteContentPanel() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = () => api.get("/site-content").then(r => {
    setSections(r.data || []);
    const initial = {};
    (r.data || []).forEach(s => { initial[s.key] = { heading: s.heading, subheading: s.subheading, body: s.body }; });
    setDrafts(initial);
    setLoading(false);
  }).catch(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const update = (key, field, value) => setDrafts(p => ({ ...p, [key]: { ...p[key], [field]: value } }));

  const save = async (key) => {
    setSavingKey(key);
    try {
      await api.put(`/site-content/${key}`, drafts[key]);
      toast.success("Section saved — live on the landing page");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSavingKey(null);
  };

  return (
    <div data-testid="admin-site-content-panel">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Site Content</h2>
        <p className="text-xs text-slate-500 mt-0.5">Edit the headings and copy that appear on your public landing page. Save publishes live.</p>
      </div>
      {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="space-y-4">
          {sections.map(s => (
            <Card key={s.key} className="border border-slate-200 rounded-none" data-testid={`site-content-${s.key}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600">{s.label}</p>
                    <p className="text-xs text-slate-500">Key: <code className="font-mono">{s.key}</code>{s.is_customised && <span className="ml-2 text-emerald-700 font-bold">· customised</span>}</p>
                  </div>
                  <Button onClick={() => save(s.key)} disabled={savingKey === s.key} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none text-xs gap-1" data-testid={`site-content-save-${s.key}`}>
                    {savingKey === s.key ? "Saving…" : "Save changes"}
                  </Button>
                </div>
                <div><Label className="text-xs">Heading</Label><Input value={drafts[s.key]?.heading || ""} onChange={e => update(s.key, "heading", e.target.value)} className="rounded-none" data-testid={`site-content-${s.key}-heading`} /></div>
                <div><Label className="text-xs">Subheading</Label><Input value={drafts[s.key]?.subheading || ""} onChange={e => update(s.key, "subheading", e.target.value)} className="rounded-none" data-testid={`site-content-${s.key}-subheading`} /></div>
                <div><Label className="text-xs">Body</Label><Textarea rows={3} value={drafts[s.key]?.body || ""} onChange={e => update(s.key, "body", e.target.value)} className="rounded-none" data-testid={`site-content-${s.key}-body`} /></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewPanel({ onJump }) {
  const [stats, setStats] = useState({ programmes: 0, gallery: 0, content: 0 });
  useEffect(() => {
    Promise.all([
      api.get("/programmes").then(r => r.data?.length || 0).catch(() => 0),
      api.get("/gallery").then(r => r.data?.length || 0).catch(() => 0),
      api.get("/site-content").then(r => r.data?.length || 0).catch(() => 0),
    ]).then(([p, g, c]) => setStats({ programmes: p, gallery: g, content: c }));
  }, []);
  const tile = (key, label, icon, count, blurb) => (
    <Card key={key} className="border border-slate-200 rounded-none cursor-pointer hover:border-amber-400 transition-colors" onClick={() => onJump(key)} data-testid={`overview-tile-${key}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">{label}</p>
            <p className="text-3xl font-bold text-slate-950 mt-1">{count}</p>
          </div>
          {icon}
        </div>
        <p className="text-xs text-slate-500 mt-3">{blurb}</p>
        <div className="text-xs text-amber-600 font-bold mt-3 flex items-center gap-1">Manage <ArrowRight size={12} /></div>
      </CardContent>
    </Card>
  );
  return (
    <div data-testid="admin-overview-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>CMS Overview</h2>
        <p className="text-xs text-slate-500 mt-0.5">Quick stats on your public-site content. Click a tile to manage.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {tile("content", "Site Content Sections", <Article size={28} className="text-slate-300" />, stats.content, "Edit hero, about, mission and other landing-page sections.")}
        {tile("programmes", "Programmes Published", <GraduationCap size={28} className="text-slate-300" />, stats.programmes, "Add, edit or retire programmes in the public catalogue.")}
        {tile("gallery", "Gallery Items", <Images size={28} className="text-slate-300" />, stats.gallery, "Upload images that appear on your public gallery page.")}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const initial = (typeof window !== "undefined" && window.location.hash) ? window.location.hash.slice(1) : "overview";
  const [tab, setTab] = useState(["overview", "content", "programmes", "universal", "gallery"].includes(initial) ? initial : "overview");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!["super_admin", "admin"].includes(user.role)) {
    return (
      <SiteAdminLayout activeTab="overview" onTabChange={() => {}}>
        <div className="p-10 max-w-md mx-auto text-center" data-testid="admin-forbidden">
          <ShieldCheck size={36} className="mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-950">Admins only</h1>
          <p className="text-sm text-slate-500 mt-1">This area is reserved for super admins and admins.</p>
        </div>
      </SiteAdminLayout>
    );
  }

  const titleMap = { overview: "Admin Overview", content: "Site Content", programmes: "Programmes Catalogue", universal: "Universal Programme Sections", gallery: "Gallery" };
  return (
    <SiteAdminLayout activeTab={tab} onTabChange={setTab}>
      <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="admin-page">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold">Public-Site CMS</p>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-950 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {titleMap[tab] || "Admin Overview"}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            This panel manages your <strong>public website</strong> only. LMS users, courses, payments and POD admin live in the <a href="/dashboard" className="text-amber-700 underline">LMS Admin</a>.
          </p>
        </div>

        {tab === "overview" && <OverviewPanel onJump={setTab} />}
        {tab === "content" && <SiteContentPanel />}
        {tab === "programmes" && <ProgrammesPanel />}
        {tab === "universal" && <UniversalSectionsPanel />}
        {tab === "gallery" && <GalleryPanel />}
      </div>
    </SiteAdminLayout>
  );
}

// Section 7 — Universal Programme Sections editor. Content here is applied to
// every programme page, so editing once cascades to all 28 (or however many)
// public programme pages without per-row work.
const UNIVERSAL_FIELDS = [
  { key: "learning_objectives", label: "Learning Objectives" },
  { key: "programme_structure", label: "Programme Structure" },
  { key: "expectations", label: "Expectations" },
  { key: "programme_timeline", label: "Programme Timeline" },
  { key: "learning_journey", label: "Learning Journey" },
  { key: "weekly_activities", label: "Weekly Activities" },
  { key: "admission_requirements", label: "Admission Requirements" },
  { key: "faq", label: "Frequently Asked Questions" },
];

function UniversalSectionsPanel() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cms/universal-sections").then(r => { setForm(r.data || {}); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cms/universal-sections", form);
      toast.success("Universal sections updated — applies to every programme page");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="universal-sections-panel">
      <div className="border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900">
        <p className="font-bold mb-1">Centralised content</p>
        <p>Edits here automatically apply to every public programme page (`/p/&#123;code&#125;`). Use the <em>Programmes Catalogue</em> tab for programme-specific copy and pricing.</p>
      </div>
      {UNIVERSAL_FIELDS.map(f => (
        <div key={f.key}>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-600 mb-1 block">{f.label}</label>
          <textarea
            value={form[f.key] || ""}
            onChange={e => setForm({ ...form, [f.key]: e.target.value })}
            rows={f.key === "faq" || f.key === "learning_journey" ? 6 : 4}
            className="w-full border border-slate-200 p-3 text-sm rounded-none font-mono"
            placeholder={`Plain text or Markdown. Used on every public programme page.`}
            data-testid={`universal-${f.key}`}
          />
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="universal-save-btn">
        {saving ? "Saving…" : "Save universal sections"}
      </Button>
    </div>
  );
}
